// In-process pub/sub for daily progress updates, keyed by
// `${profileId}_${dateKey}`. The sync engine emits after every
// remote-driven `saveDailyProgress` so that any mounted
// `useRoutineProgress` for the same (profile, day) refreshes without
// re-reading IDB. Local taps deliberately do not emit — the hook
// already updates state optimistically before flushing.

import type { DailyProgress, DateKey, Id } from '../../lib/types';

export type ProgressListener = (daily: DailyProgress) => void;

const listeners = new Map<string, Set<ProgressListener>>();

function key(profileId: Id, dateKey: DateKey): string {
  return `${profileId}_${dateKey}`;
}

export function subscribeProgress(
  profileId: Id,
  dateKey: DateKey,
  listener: ProgressListener,
): () => void {
  const k = key(profileId, dateKey);
  let set = listeners.get(k);
  if (!set) {
    set = new Set();
    listeners.set(k, set);
  }
  set.add(listener);
  return () => {
    const current = listeners.get(k);
    if (!current) return;
    current.delete(listener);
    if (current.size === 0) listeners.delete(k);
  };
}

export function emitProgress(daily: DailyProgress): void {
  const set = listeners.get(key(daily.profileId, daily.dateKey));
  console.info('[progress:events] fan-out', {
    profile: daily.profileId,
    dateKey: daily.dateKey,
    listenerCount: set?.size ?? 0,
  });
  if (!set) return;
  for (const listener of set) {
    try {
      listener(daily);
    } catch (err) {
      console.error('[progress:events] listener threw', err);
    }
  }
}
